import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useRef, type ComponentPropsWithoutRef } from 'react'
import { twMerge } from 'tailwind-merge'

/**
 * The dropdown menu surface: white card, brand border, shadow, and slide-in entrance. Chrome only, no inner
 * padding, so children (MenuItemGroups and full-bleed slots like headers/footers) control their own layout.
 * z-[1050] sits above modal content (z-[1002]) so in-modal menus aren't occluded, and below toasts (z-[1100]).
 */
const menuSurfaceClassName = twMerge(
  'z-[1050] flex min-w-[150px] flex-col overflow-hidden rounded-lg border border-border-default bg-surface-popover shadow-menu',
  'will-change-[opacity,transform]',
  'data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade',
  'data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade'
)

export type MenuContentProps = ComponentPropsWithoutRef<typeof DropdownMenu.Content>

/**
 * The dropdown menu surface. Focus returns to the trigger when the menu closes by Escape or by selecting an item,
 * but not when it closes from a click outside, since the pointer has already moved on.
 */
export function MenuContent({ children, className, sideOffset = 2, onCloseAutoFocus, onInteractOutside, ...props }: MenuContentProps) {
  const interactedOutsideRef = useRef(false)

  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        sideOffset={sideOffset}
        className={twMerge(menuSurfaceClassName, className)}
        data-ds-component="MenuContent"
        onInteractOutside={(event) => {
          onInteractOutside?.(event)
          interactedOutsideRef.current = true
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event)
          if (interactedOutsideRef.current) event.preventDefault()
          interactedOutsideRef.current = false
        }}
        {...props}
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  )
}

/** A padded group of MenuItems inside a MenuContent. Separate groups are divided by a MenuSeparator. */
export function MenuItemGroup({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={twMerge('flex flex-col gap-0.5 p-2', className)} data-ds-component="MenuItemGroup" {...props}>
      {children}
    </div>
  )
}

/** Full-width divider between groups/slots in a MenuContent. Runs edge to edge since the surface has no padding. */
export function MenuSeparator({ className, ...props }: ComponentPropsWithoutRef<typeof DropdownMenu.Separator>) {
  return <DropdownMenu.Separator className={twMerge('h-px bg-border-default', className)} data-ds-component="MenuSeparator" {...props} />
}
